<template>
  <div>
    <div class="notes-grid">
      <!-- Post-it para agregar nota -->
      <div class="post-it post-it--add">
        <textarea
          v-model="new_note"
          class="post-it__textarea"
          :placeholder="$t('campaigns.notes_placeholder')"
          maxlength="300"
          rows="4"
        />
        <div class="post-it__add-footer">
          <small class="post-it__char-count">{{ new_note.length }}/300</small>
          <button
            class="post-it__save-btn"
            :disabled="new_note.trim().length === 0"
            @click="saveNote"
          >
            <feather-icon icon="PlusIcon" size="14" />
          </button>
        </div>
      </div>

      <!-- Notas existentes -->
      <div
        v-for="(note, index) in notes"
        :key="note.uuid"
        :class="`post-it post-it--${(index % 4) + 1}`"
      >
        <template v-if="editing_uuid === note.uuid">
          <textarea
            v-model="editing_text"
            ref="editing_textarea"
            class="post-it__textarea post-it__textarea--editing"
            maxlength="300"
            rows="4"
            @keydown.esc="cancelEdit"
          />
          <div class="post-it__actions">
            <button class="post-it__action-btn post-it__action-btn--save" @click="saveEdit(note.uuid)">
              <feather-icon icon="CheckIcon" size="13" />
            </button>
            <button class="post-it__action-btn post-it__action-btn--cancel" @click="cancelEdit">
              <feather-icon icon="XIcon" size="13" />
            </button>
          </div>
        </template>
        <template v-else>
          <p class="post-it__text">{{ note.note }}</p>
          <div class="post-it__footer">
            <small class="post-it__date">{{ formatDate(note.created_at) }}</small>
            <div class="post-it__icons">
              <button class="post-it__icon-btn" @click="startEdit(note)">
                <feather-icon icon="Edit2Icon" size="13" />
              </button>
              <button class="post-it__icon-btn post-it__icon-btn--delete" @click="confirmDelete(note.uuid)">
                <feather-icon icon="TrashIcon" size="13" />
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>
    <div v-if="notes.length === 0" class="text-center text-muted pb-2" style="font-size:0.8rem">
      {{ $t('campaigns.notes_empty') }}
    </div>
    <div v-if="total_pages > 1" class="notes-pagination">
      <button
        class="notes-pagination__btn"
        :disabled="current_page <= 1"
        @click="$emit('change_page', current_page - 1)"
      >
        <feather-icon icon="ChevronLeftIcon" size="16" />
      </button>
      <small class="notes-pagination__info">{{ current_page }} / {{ total_pages }}</small>
      <button
        class="notes-pagination__btn"
        :disabled="current_page >= total_pages"
        @click="$emit('change_page', current_page + 1)"
      >
        <feather-icon icon="ChevronRightIcon" size="16" />
      </button>
    </div>
  </div>
</template>

<script>

export default {
  name: 'NotesContent',
  components: {
    FeatherIcon: () => import('@/@core/components/feather-icon/FeatherIcon.vue'),
  },
  props: {
    notes: {
      type: Array,
      default: () => [],
    },
    total_count: {
      type: Number,
      default: 0,
    },
    current_page: {
      type: Number,
      default: 1,
    },
    page_size: {
      type: Number,
      default: 5,
    },
  },
  computed: {
    total_pages() {
      return Math.ceil(this.total_count / this.page_size)
    },
  },
  data() {
    return {
      new_note: '',
      editing_uuid: null,
      editing_text: '',
    }
  },
  methods: {
    formatDate(dateStr) {
      return new Date(dateStr).toLocaleDateString(this.$i18n.locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    saveNote() {
      if (this.new_note.trim().length === 0) return
      this.$emit('add_note', this.new_note.trim())
      this.new_note = ''
    },
    startEdit(note) {
      this.editing_uuid = note.uuid
      this.editing_text = note.note
      this.$nextTick(() => {
        if (this.$refs.editing_textarea) {
          const el = Array.isArray(this.$refs.editing_textarea) ? this.$refs.editing_textarea[0] : this.$refs.editing_textarea
          el.focus()
          el.setSelectionRange(el.value.length, el.value.length)
        }
      })
    },
    cancelEdit() {
      this.editing_uuid = null
      this.editing_text = ''
    },
    saveEdit(uuid) {
      if (this.editing_text.trim().length === 0) return
      this.$emit('edit_note', { uuid, note: this.editing_text.trim() })
      this.cancelEdit()
    },
    confirmDelete(uuid) {
      this.$swal({
        title: this.$t('campaigns.notes_delete_title'),
        text: this.$t('campaigns.notes_delete_text'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('campaigns.notes_delete_confirm'),
        cancelButtonText: this.$t('campaigns.msjCancel'),
        customClass: {
          confirmButton: 'btn confirm-button',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
        heightAuto: false,
      }).then(result => {
        if (result.value) this.$emit('delete_note', uuid)
      })
    },
  },
}
</script>

<style scoped lang="scss">
$post-it-colors: (
  1: #fff176,
  2: #a5d6a7,
  3: #90caf9,
  4: #ffcc80,
);

.notes-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 0.5rem 0;
}

.post-it {
  position: relative;
  width: calc(50% - 0.5rem);
  min-height: 120px;
  padding: 1rem 1rem 2rem;
  border-radius: 2px;
  box-shadow: 3px 3px 10px rgba(0, 0, 0, 0.18), 1px 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @each $n, $color in $post-it-colors {
    &--#{$n} {
      background-color: $color;
    }
  }

  &--add {
    background-color: #fafafa;
    border: 2px dashed #cbd5e0;
    box-shadow: none;
    padding: 0.75rem;
    justify-content: flex-start;
    gap: 0.5rem;
  }

  &:not(&--add):nth-child(odd) {
    transform: rotate(-1deg);
  }
  &:not(&--add):nth-child(even) {
    transform: rotate(0.8deg);
  }

  &:not(&--add)::before {
    content: '';
    position: absolute;
    top: -6px;
    left: 50%;
    transform: translateX(-50%);
    width: 18px;
    height: 12px;
    background: rgba(0, 0, 0, 0.15);
    border-radius: 2px;
  }

  &__text {
    font-size: 0.88rem;
    line-height: 1.5;
    color: #3d3d3d;
    margin: 0 0 0.5rem;
    word-break: break-word;
    font-family: 'avenir-medium', sans-serif;
  }

  &__date {
    font-size: 0.72rem;
    color: rgba(0, 0, 0, 0.5);
    align-self: flex-end;
  }

  &__footer {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-top: auto;
  }

  &__icons {
    display: flex;
    gap: 0.25rem;
    opacity: 0;
    transition: opacity 150ms;
  }

  &:hover &__icons {
    opacity: 1;
  }

  &__icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 4px;
    border: none;
    background: rgba(0, 0, 0, 0.08);
    cursor: pointer;
    color: #555;
    transition: background 150ms;

    &:hover {
      background: rgba(0, 0, 0, 0.18);
    }

    &--delete:hover {
      background: rgba(220, 53, 69, 0.18);
      color: #dc3545;
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.3rem;
    margin-top: 0.4rem;
  }

  &__action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 4px;
    border: none;
    cursor: pointer;
    transition: background 150ms;

    &--save {
      background: rgba(40, 167, 69, 0.15);
      color: #28a745;
      &:hover { background: rgba(40, 167, 69, 0.3); }
    }
    &--cancel {
      background: rgba(0, 0, 0, 0.08);
      color: #555;
      &:hover { background: rgba(0, 0, 0, 0.18); }
    }
  }

  &__textarea--editing {
    background: rgba(255,255,255,0.5);
    border-radius: 4px;
    padding: 0.25rem;
  }

  &__textarea {
    flex: 1;
    border: none;
    background: transparent;
    resize: none;
    font-size: 0.88rem;
    line-height: 1.5;
    color: #3d3d3d;
    font-family: 'avenir-medium', sans-serif;
    width: 100%;
    outline: none;

    &::placeholder {
      color: #a0aec0;
    }
  }

  &__add-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 0.5rem;
  }

  &__char-count {
    font-size: 0.7rem;
    color: #a0aec0;
  }

  &__save-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: none;
    background-color: #0096fe;
    color: white;
    cursor: pointer;
    transition: background 200ms;

    &:disabled {
      background-color: #cbd5e0;
      cursor: not-allowed;
    }

    &:not(:disabled):hover {
      background-color: #007ad4;
    }
  }
}

.notes-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #eff3f6;
  margin-top: 0.5rem;

  &__btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: 1px solid #e2e8f0;
    background: white;
    cursor: pointer;
    transition: all 200ms;
    color: #4a5568;

    &:disabled {
      opacity: 0.35;
      cursor: not-allowed;
    }

    &:not(:disabled):hover {
      background-color: #f7fafc;
      border-color: #0096fe;
      color: #0096fe;
    }
  }

  &__info {
    color: #a0aec0;
    font-size: 0.78rem;
    min-width: 40px;
    text-align: center;
  }
}
</style>
